import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Package, Truck, CheckCircle2, Factory, RefreshCw, Loader2, ChevronDown } from "lucide-react";

const STATUS_STYLES = {
  pending_fulfillment: "bg-amber-50 text-amber-800 border-amber-200",
  pending_submission: "bg-blue-50 text-blue-800 border-blue-200",
  pf_created: "bg-blue-50 text-blue-800 border-blue-200",
  pf_in_production: "bg-indigo-50 text-indigo-800 border-indigo-200",
  pf_shipped: "bg-green-50 text-green-800 border-green-200",
  shipped: "bg-green-50 text-green-800 border-green-200",
  pf_delivered: "bg-emerald-50 text-emerald-800 border-emerald-200",
  delivered: "bg-emerald-50 text-emerald-800 border-emerald-200",
  pf_canceled: "bg-red-50 text-red-800 border-red-200",
};

const STATUS_LABEL = {
  pending_fulfillment: "Awaiting shipment (in-house)",
  pending_submission: "Awaiting Printify submission",
  pf_created: "Received by Printify",
  pf_in_production: "In production",
  pf_shipped: "Shipped",
  shipped: "Shipped",
  pf_delivered: "Delivered",
  delivered: "Delivered",
  pf_canceled: "Canceled",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || "bg-neutral-100 text-neutral-700 border-neutral-200";
  return <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 ${cls}`}>{STATUS_LABEL[status] || status}</span>;
}

export default function OrderDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shipInputs, setShipInputs] = useState({}); // { [order_id]: { tracking, carrier } }

  useEffect(() => { if (!loading && (!user || !user.is_admin)) nav("/auth"); }, [user, loading, nav]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/orders");
      setOrders(r.data.orders || []);
    } catch { toast.error("Failed to load orders"); }
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const shipSelf = async (order_id) => {
    const inp = shipInputs[order_id];
    if (!inp?.tracking) { toast.error("Enter a tracking number"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order_id}/ship-self`, { tracking_number: inp.tracking, carrier: inp.carrier || "USPS" });
      toast.success("In-house shipment marked & email sent");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ship failed");
    } finally { setBusy(false); }
  };

  if (!user?.is_admin) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="orders-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-5xl text-[color:var(--hb-navy)]">Orders</h1>
          <p className="text-sm text-neutral-600 mt-2">Track hybrid fulfillment across in-house and Printify sub-orders.</p>
        </div>
        <button onClick={load} className="text-xs uppercase tracking-widest border border-neutral-300 px-3 py-1 flex items-center gap-1" data-testid="refresh-orders-btn">
          <RefreshCw size={12}/> Refresh
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="mt-10 text-center py-16 border border-dashed border-neutral-300 text-sm text-neutral-500" data-testid="orders-empty">
          No orders yet — you'll see them here after your first sale.
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {orders.map((o) => {
            const isOpen = expanded === o.order_id;
            const subs = o.sub_orders || [];
            const hasSplit = subs.length > 1;
            return (
              <div key={o.order_id} className="border border-neutral-200 bg-white" data-testid={`order-${o.order_id}`}>
                <button onClick={() => setExpanded(isOpen ? null : o.order_id)} className="w-full flex items-center gap-4 p-4 text-left hover:bg-neutral-50 transition">
                  <Package size={18} className="text-[color:var(--hb-royal)]"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{o.email || "Guest"}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                      #{o.order_id} · ${(o.amount || 0).toFixed(2)} · {new Date(o.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-2">
                    {subs.map((s, i) => <StatusBadge key={i} status={s.status}/>)}
                    {hasSplit && <span className="text-[10px] tracking-widest text-neutral-500 uppercase">Split</span>}
                  </div>
                  <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`}/>
                </button>
                {isOpen && (
                  <div className="border-t border-neutral-100 p-5 space-y-4" data-testid={`order-${o.order_id}-details`}>
                    {/* Shipping address */}
                    {o.shipping_address && (
                      <div className="text-xs text-neutral-600" data-testid="ship-address">
                        <div className="uppercase tracking-widest text-[10px] text-neutral-500 mb-1">Ship to</div>
                        {o.shipping_address.name && <div className="text-neutral-800">{o.shipping_address.name}</div>}
                        {o.shipping_address.address && (
                          <div>
                            {o.shipping_address.address.line1} {o.shipping_address.address.line2 || ""}<br/>
                            {o.shipping_address.address.city}, {o.shipping_address.address.state} {o.shipping_address.address.postal_code}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Each sub-order */}
                    {subs.map((s, i) => (
                      <div key={i} className={`border ${s.fulfillment_source === "SELF" ? "border-[color:var(--hb-royal)]" : "border-neutral-300"} p-4`} data-testid={`sub-${s.fulfillment_source}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {s.fulfillment_source === "SELF" ? <Factory size={14} className="text-[color:var(--hb-royal)]"/> : <Truck size={14} className="text-neutral-600"/>}
                          <span className="text-xs uppercase tracking-widest font-medium">
                            {s.fulfillment_source === "SELF" ? "In-House Fulfillment" : "Printify Print-on-Demand"}
                          </span>
                          <StatusBadge status={s.status}/>
                        </div>
                        <div className="text-xs text-neutral-700 mb-2">
                          {s.items.map((it, j) => (
                            <div key={j}>{it.name} · {it.color} · {it.size} × {it.qty} <span className="text-neutral-500">(${it.line_total?.toFixed(2)})</span></div>
                          ))}
                        </div>
                        <div className="text-[11px] text-neutral-500">Shipping: ${(s.shipping || 0).toFixed(2)}</div>
                        {s.tracking_number && (
                          <div className="mt-2 text-xs bg-green-50 border border-green-200 p-2 flex items-center gap-2">
                            <CheckCircle2 size={12} className="text-green-700"/>
                            <span>Tracking: <strong>{s.tracking_number}</strong>{s.carrier ? ` · ${s.carrier}` : ""}</span>
                          </div>
                        )}
                        {/* SELF ship action */}
                        {s.fulfillment_source === "SELF" && s.status === "pending_fulfillment" && (
                          <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="ship-self-form">
                            <input
                              type="text"
                              placeholder="Tracking number"
                              value={shipInputs[o.order_id]?.tracking || ""}
                              onChange={(e) => setShipInputs(p => ({...p, [o.order_id]: {...p[o.order_id], tracking: e.target.value}}))}
                              className="flex-1 min-w-[180px] border border-neutral-300 px-2 py-1.5 text-sm"
                              data-testid="tracking-input"
                            />
                            <select
                              value={shipInputs[o.order_id]?.carrier || "USPS"}
                              onChange={(e) => setShipInputs(p => ({...p, [o.order_id]: {...p[o.order_id], carrier: e.target.value}}))}
                              className="border border-neutral-300 px-2 py-1.5 text-sm"
                              data-testid="carrier-select"
                            >
                              <option value="USPS">USPS</option>
                              <option value="UPS">UPS</option>
                              <option value="FedEx">FedEx</option>
                              <option value="DHL">DHL</option>
                            </select>
                            <button
                              onClick={() => shipSelf(o.order_id)}
                              disabled={busy}
                              className="text-xs uppercase tracking-widest bg-[color:var(--hb-navy)] text-white px-4 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
                              data-testid="mark-shipped-btn"
                            >
                              {busy ? <Loader2 size={12} className="animate-spin"/> : <Truck size={12}/>} Mark Shipped
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {hasSplit && (
                      <p className="text-[10px] text-neutral-500 italic">Customer will receive separate shipment notifications for each portion of this order.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 flex gap-3 text-xs">
        <Link to="/admin" className="text-[color:var(--hb-royal)] uppercase tracking-widest" data-testid="back-to-admin">← Admin home</Link>
      </div>
    </div>
  );
}
